import { api } from './client';

/**
 * Viral-invite referral redemption (the game-level invite). The invitee
 * follows a shared {@code goodtime.casino/i/CODE} link; on first launch the
 * app reads the code and POSTs it here, which resolves the inviter and credits
 * both sides via the server's invite-reward ledger.
 */

const PENDING_KEY = 'goodtime.referral.pending';
const REDEEMED_KEY = 'goodtime.referral.redeemed';

export type RedeemStatus = 'OK' | 'INVALID_CODE' | 'SELF_REFERRAL';

/** POST the code for the current player. Idempotent server-side (deterministic
 *  command id + per-pair dedup), so retrying on a later launch is safe. */
export function redeemReferral(
  code: string,
  playerId: string,
  displayName: string,
): Promise<{ status: RedeemStatus }> {
  return api<{ status: RedeemStatus }>('/invites/redeem', {
    method: 'POST',
    body: JSON.stringify({ code, playerId, displayName }),
  });
}

/** Read a referral code from the launch URL — `?i=CODE` / `?ref=CODE` query or
 *  a `/i/CODE` path — then strip it from the address bar so a refresh can't
 *  re-trigger. Returns the upper-cased code, or null. Call once at startup. */
export function readLaunchReferralCode(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const u = new URL(window.location.href);
    let code = u.searchParams.get('i') || u.searchParams.get('ref');
    if (!code) {
      const m = u.pathname.match(/\/i\/([A-Za-z0-9]+)/);
      code = m?.[1] ?? null;
    }
    if (!code) return null;
    u.searchParams.delete('i');
    u.searchParams.delete('ref');
    const path = u.pathname.replace(/\/i\/[A-Za-z0-9]+\/?/, '/');
    const qs = u.searchParams.toString();
    window.history.replaceState({}, '', path + (qs ? `?${qs}` : ''));
    return code.toUpperCase();
  } catch {
    return null;
  }
}

let redeemInFlight = false;

/**
 * Drive the one-time redemption: persist a freshly-captured launch code,
 * redeem the pending code once the player exists, and on success run
 * {@code onCredited} (the wallet re-sync that surfaces the +gems toast).
 * A device redeems at most one referral; transient network errors keep the
 * code pending for the next launch.
 */
export async function redeemPendingReferral(
  launchCode: string | null,
  playerId: string,
  displayName: string,
  onCredited: () => Promise<void>,
): Promise<void> {
  if (typeof localStorage === 'undefined') return;
  if (redeemInFlight || localStorage.getItem(REDEEMED_KEY) === '1') return;
  if (launchCode) localStorage.setItem(PENDING_KEY, launchCode);
  const code = localStorage.getItem(PENDING_KEY);
  if (!code) return;

  redeemInFlight = true;
  try {
    const { status } = await redeemReferral(code, playerId, displayName);
    if (status === 'OK') {
      localStorage.setItem(REDEEMED_KEY, '1');
      localStorage.removeItem(PENDING_KEY);
      await onCredited();
    } else if (status === 'SELF_REFERRAL') {
      localStorage.setItem(REDEEMED_KEY, '1'); // can't refer yourself — stop
      localStorage.removeItem(PENDING_KEY);
    } else {
      localStorage.removeItem(PENDING_KEY); // bad code — drop it, don't block future
    }
  } catch {
    // Network hiccup — leave the code pending so the next launch retries.
  } finally {
    redeemInFlight = false;
  }
}
